/**
 * Authentication API Module
 *
 * Handles all authentication flows:
 * - Portal (Individual/Teams)
 * - Customer (Enterprise - license key)
 * - Admin
 * - Service Integrator
 * - Enterprise User (username/password)
 * - SSO (SAML)
 */

import { baseClient, http } from './base';
import type { AdminSession, CustomerSession, ServiceIntegratorSession, ServiceIntegrator } from '../types';

// ===== PORTAL TYPES =====

export interface PortalUser {
  id: number;
  email: string;
  name?: string;
  avatarUrl?: string;
  emailVerified: boolean;
  hasPassword: boolean;
  hasGoogle: boolean;
  hasGithub: boolean;
  language?: 'en' | 'nl';
  portalRole?: 'user' | 'marketing';
}

export interface PortalOrganization {
  id: number;
  name: string;
  slug: string;
  planType: 'individual' | 'team' | 'enterprise';
  licenseKey?: string;
}

export interface PortalSubscription {
  plan: 'individual' | 'team';
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'free';
  developerSeats: number;
  stakeholderSeats: number;
  trialEndsAt?: number;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd: boolean;
  preselectedModules?: string[];
  activeModuleIds?: string[];
}

export interface PortalSession {
  token: string;
  user: PortalUser;
  organization: PortalOrganization | null;
  subscription: PortalSubscription | null;
  /**
   * Effective feature list, computed backend-side by `effectiveFeatures`
   * (portal-auth.ts `buildSessionResponse`) — the SAME list the JWT carries.
   * Top-level, NOT nested under `subscription`: an org without a
   * `subscriptions` row gets `subscription: null` but still has the full
   * free-tier capability set. Gate capabilities on this, never on
   * `subscription.activeModuleIds` (which only says what was PURCHASED).
   */
  activeFeatures?: string[];
  role?: 'owner' | 'admin' | 'developer' | 'stakeholder';
}

// ===== ENTERPRISE USER TYPES =====

export interface DirectUser {
  id: string;
  username: string;
  email: string;
  role: 'developer' | 'stakeholder' | 'admin';
  emailVerified: boolean;
  customerId: number;
  lastLoginAt?: number;
}

export interface EnterpriseCustomer {
  id: number;
  name: string;
  slug?: string;
  company?: string;
  status: string;
  theme?: string;
  isServiceIntegrator: boolean;
  licenseKey: string;
}

export interface DirectUserSession {
  token: string;
  user: DirectUser;
  customer: EnterpriseCustomer | null;
}

// ===== SSO TYPES =====

export interface SsoConfigCheck {
  success: boolean;
  enabled: boolean;
  provider: string | null;
  customerName?: string;
  message?: string;
}

export interface SsoSession {
  customerId: number;
  userId: string;
  email: string;
  displayName: string;
  nameId: string;
  sessionIndex?: string;
  attributes?: Record<string, unknown>;
  expiresAt: number;
  lastActivity: number;
}

export interface SsoSessionInfo {
  id: number;
  customerId: number;
  userId: string;
  email: string;
  displayName: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: number;
  lastActivity: number;
  expiresAt: number;
}

export interface SsoConfig {
  id: number;
  customerId: number;
  enabled: boolean;
  provider: string;
  entryPoint: string;
  issuer: string;
  callbackUrl: string;
  logoutUrl?: string;
  nameIdFormat?: string;
  wantAssertionsSigned: boolean;
  wantAuthnResponseSigned?: boolean;
  signatureAlgorithm?: string;
  attributeMapping?: string;
  certConfigured: boolean;
  jitEnabled: boolean;
  jitDefaultRole: 'developer' | 'stakeholder';
  jitAutoApprove: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface CreateSsoConfigDto {
  enabled?: boolean;
  provider: 'saml';
  entryPoint: string;
  issuer: string;
  cert: string;
  callbackUrl: string;
  logoutUrl?: string;
  nameIdFormat?: string;
  wantAssertionsSigned?: boolean;
  wantAuthnResponseSigned?: boolean;
  signatureAlgorithm?: string;
  attributeMapping?: Record<string, string>;
  jitEnabled?: boolean;
  jitDefaultRole?: 'developer' | 'stakeholder';
  jitAutoApprove?: boolean;
}

export interface SsoStats {
  totalLogins: number;
  uniqueUsers: number;
  activeSessionsCount: number;
  loginsByDay: Array<{ date: string; count: number }>;
}

// ===== AUTH API =====

export const authApi = {
  // ===== PORTAL AUTHENTICATION (Individual/Teams) =====

  async portalRegister(data: {
    email: string;
    password: string;
    name?: string;
    plan?: 'individual' | 'team';
    // Company info (required for Teams plan)
    companyName?: string;
    billingAddress?: string;
    vatNumber?: string;
    country?: string;
    trialToken?: string;
    turnstileToken?: string;
    language?: 'en' | 'nl';
  }): Promise<{ success: boolean; message: string; requiresVerification: boolean; emailSent?: boolean; token?: string; user?: PortalUser; organization?: PortalOrganization | null; subscription?: PortalSubscription | null; activeFeatures?: string[]; role?: 'owner' | 'admin' | 'developer' | 'stakeholder' }> {
    const { data: result } = await http.post('/api/portal/auth/register', data);
    if (result.token) {
      baseClient.setPortalToken(result.token);
    }
    return result;
  },

  async validateTrialToken(token: string): Promise<{
    valid: boolean;
    reason?: string;
    email?: string;
    trialMonths?: number;
    planType?: 'individual' | 'team' | 'enterprise';
    expiresAt?: number;
  }> {
    const { data } = await http.get('/api/portal/auth/validate-trial-token', {
      params: { token },
    });
    return data;
  },

  async portalVerifyEmail(token: string): Promise<PortalSession> {
    const { data } = await http.post('/api/portal/auth/verify-email', { token });

    // Clear all other session tokens to prevent mixed auth state
    localStorage.removeItem('customer_token');
    localStorage.removeItem('service_integrator_token');
    localStorage.removeItem('direct_user_token');
    localStorage.removeItem('sso_token');

    if (data.token) {
      baseClient.setPortalToken(data.token);
    }
    return {
      token: data.token,
      user: data.user,
      organization: data.organization,
      subscription: data.subscription || null,
      activeFeatures: data.activeFeatures,
      role: data.role,
    };
  },

  async portalResendVerification(email: string): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post('/api/portal/auth/resend-verification', { email });
    return data;
  },

  async portalLogin(email: string, password: string, turnstileToken?: string | null): Promise<PortalSession> {
    const { data } = await http.post('/api/portal/auth/login', { email, password, 'cf-turnstile-response': turnstileToken });

    // Clear all other session tokens to prevent mixed auth state
    localStorage.removeItem('customer_token');
    localStorage.removeItem('service_integrator_token');
    localStorage.removeItem('direct_user_token');
    localStorage.removeItem('sso_token');

    if (data.token) {
      baseClient.setPortalToken(data.token);
    }
    return {
      token: data.token,
      user: data.user,
      organization: data.organization,
      subscription: data.subscription,
      activeFeatures: data.activeFeatures,
      role: data.role,
    };
  },

  async portalMagicLinkRequest(email: string): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post('/api/portal/auth/magic-link', { email });
    return data;
  },

  async portalMagicLinkVerify(token: string): Promise<PortalSession> {
    const { data } = await http.post('/api/portal/auth/magic-link/verify', { token });

    // Clear all other session tokens to prevent mixed auth state
    localStorage.removeItem('customer_token');
    localStorage.removeItem('service_integrator_token');
    localStorage.removeItem('direct_user_token');
    localStorage.removeItem('sso_token');

    if (data.token) {
      baseClient.setPortalToken(data.token);
    }
    return {
      token: data.token,
      user: data.user,
      organization: data.organization,
      subscription: data.subscription,
      activeFeatures: data.activeFeatures,
      role: data.role,
    };
  },

  // Both password-reset endpoints sit behind turnstile.passwordReset with
  // required:true, so the CAPTCHA token has to ride along exactly like it
  // does on login/register — the widget must be rendered with
  // action="password-reset" or the backend rejects on action mismatch.
  async portalForgotPassword(email: string, turnstileToken?: string | null): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post('/api/portal/auth/forgot-password', { email, 'cf-turnstile-response': turnstileToken });
    return data;
  },

  async portalResetPassword(token: string, password: string, turnstileToken?: string | null): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post('/api/portal/auth/reset-password', { token, password, 'cf-turnstile-response': turnstileToken });
    return data;
  },

  async portalOAuthGoogle(code: string, state?: string): Promise<PortalSession> {
    const { data } = await http.post('/api/portal/auth/oauth/google/callback', { code, state });

    // Clear all other session tokens to prevent mixed auth state
    localStorage.removeItem('customer_token');
    localStorage.removeItem('service_integrator_token');
    localStorage.removeItem('direct_user_token');
    localStorage.removeItem('sso_token');

    if (data.token) {
      baseClient.setPortalToken(data.token);
    }
    return {
      token: data.token,
      user: data.user,
      organization: data.organization,
      subscription: data.subscription,
      activeFeatures: data.activeFeatures,
      role: data.role,
    };
  },

  async portalOAuthGithub(code: string, state?: string): Promise<PortalSession> {
    const { data } = await http.post('/api/portal/auth/oauth/github/callback', { code, state });

    // Clear all other session tokens to prevent mixed auth state
    localStorage.removeItem('customer_token');
    localStorage.removeItem('service_integrator_token');
    localStorage.removeItem('direct_user_token');
    localStorage.removeItem('sso_token');

    if (data.token) {
      baseClient.setPortalToken(data.token);
    }
    return {
      token: data.token,
      user: data.user,
      organization: data.organization,
      subscription: data.subscription,
      activeFeatures: data.activeFeatures,
      role: data.role,
    };
  },

  async portalGetSession(): Promise<PortalSession> {
    const { data } = await http.get('/api/portal/auth/me');
    return {
      token: baseClient.getPortalToken()!,
      user: data.user,
      organization: data.organization,
      subscription: data.subscription,
      activeFeatures: data.activeFeatures,
      role: data.role,
    };
  },

  async getOrganizations(): Promise<{ id: number; name: string; planType: string; role: string; isCurrent: boolean }[]> {
    const { data } = await http.get('/api/portal/auth/organizations');
    return data.organizations;
  },

  async switchOrganization(organizationId: number): Promise<PortalSession> {
    const { data } = await http.post('/api/portal/auth/switch-organization', { organizationId });
    baseClient.setPortalToken(data.token);
    // Keep impersonation marker in sync so reload doesn't clear the new token
    if (sessionStorage.getItem('impersonation_token')) {
      sessionStorage.setItem('impersonation_token', data.token);
    }
    return {
      token: data.token,
      user: data.user,
      organization: data.organization,
      subscription: data.subscription,
      activeFeatures: data.activeFeatures,
      role: data.role,
    };
  },

  async portalOAuthCallback(token: string): Promise<PortalSession> {
    // Clear all other session tokens to prevent mixed auth state
    localStorage.removeItem('customer_token');
    localStorage.removeItem('service_integrator_token');
    localStorage.removeItem('direct_user_token');
    localStorage.removeItem('sso_token');

    baseClient.setPortalToken(token);
    return this.portalGetSession();
  },

  async portalLogout(): Promise<void> {
    try {
      await http.post('/api/portal/auth/logout');
    } finally {
      localStorage.removeItem('portal_token');
    }
  },

  // ===== ADMIN AUTHENTICATION =====

  async adminLogin(adminKey: string): Promise<AdminSession> {
    const { data } = await http.post('/api/auth/admin/login', { adminKey });
    return data.session;
  },

  async adminLogout(): Promise<void> {
    await http.post('/api/auth/admin/logout');
    baseClient.clearAuth();
  },

  async getAdminSession(): Promise<AdminSession> {
    const { data } = await http.get('/api/auth/admin/session');
    return data.session;
  },

  // ===== CROSS-DOMAIN TOKEN EXCHANGE =====

  async requestCrossDomainToken(token: string, sessionPayload: string, sessionType: 'customer' | 'service-integrator'): Promise<{ oneTimeToken: string; expiresIn: number }> {
    const { data } = await http.post('/api/auth/cross-domain-token', {
      token,
      sessionPayload,
      sessionType,
    });
    return data;
  },

  async exchangeOneTimeToken(oneTimeToken: string): Promise<any> {
    const { data } = await http.post('/api/auth/exchange-token', { oneTimeToken });

    if (data.token) {
      if (data.sessionType === 'service-integrator') {
        // Clear all other session tokens
        localStorage.removeItem('customer_token');
        localStorage.removeItem('portal_token');
        localStorage.removeItem('direct_user_token');
        localStorage.removeItem('sso_token');
        baseClient.setServiceIntegratorToken(data.token);
      } else {
        // Clear all other session tokens
        localStorage.removeItem('service_integrator_token');
        localStorage.removeItem('portal_token');
        localStorage.removeItem('direct_user_token');
        localStorage.removeItem('sso_token');
        baseClient.setCustomerToken(data.token);
      }
    }

    return data;
  },

  // ===== SSO AUTHENTICATION =====

  async ssoCheckConfig(customerId: number): Promise<SsoConfigCheck> {
    const { data } = await http.get(`/api/sso/config/check/${customerId}`);
    return data;
  },

  getSsoLoginUrl(customerId: number): string {
    const baseUrl = http.defaults.baseURL || '';
    return `${baseUrl}/api/sso/login/${customerId}`;
  },

  async ssoValidateSession(token: string): Promise<SsoSession> {
    const { data } = await http.post('/api/sso/session/validate', { token });
    return data;
  },

  async ssoLogout(): Promise<void> {
    await http.post('/api/sso/logout');
    localStorage.removeItem('sso_token');
  },

  // ===== SSO ADMIN CONFIGURATION =====

  async getSsoConfig(): Promise<SsoConfig | null> {
    try {
      const { data } = await http.get('/api/sso/config');
      return data.success ? data.config : null;
    } catch {
      return null;
    }
  },

  async saveSsoConfig(config: CreateSsoConfigDto): Promise<{ success: boolean; message: string; customerId?: number }> {
    const { data } = await http.post('/api/sso/config', config);
    return data;
  },

  async deleteSsoConfig(): Promise<{ success: boolean; message: string }> {
    const { data } = await http.delete('/api/sso/config');
    return data;
  },

  async getSpMetadata(customerId: number): Promise<string> {
    const response = await http.get(`/api/sso/metadata/${customerId}`, {
      responseType: 'text'
    });
    return response.data;
  },

  async getSsoSessions(): Promise<SsoSessionInfo[]> {
    const { data } = await http.get('/api/sso/sessions');
    return data.success ? data.sessions : [];
  },

  async getSsoStats(days: number = 30): Promise<SsoStats> {
    const { data } = await http.get('/api/sso/stats', { params: { days } });
    return data.stats;
  },

  async parseIdpMetadata(metadataXml: string): Promise<Partial<CreateSsoConfigDto>> {
    const { data } = await http.post('/api/sso/parse-metadata', { metadata: metadataXml });
    return data.config;
  },
};
